import { newFinancialRate } from '@orus.eu/amount'
import { computeAgeFromFutureCalendarDate } from '@orus.eu/calendar-date'

import { createPolicyItemConfigurationResolver } from '../../../model/policy-item-configuration.ts'
import type { PolicyItems } from '../../../model/policy-item.ts'
import type { FrPrelPolicyItems, FrPrelPolicyItemsState } from '../fr-prel-insurance.ts'

const tenPercent = newFinancialRate('0.1')
const twentyPercent = newFinancialRate('0.2')
const thirtyPercent = newFinancialRate('0.3')
const fiftyPercent = newFinancialRate('0.5')

export const ittIppDeductible = {
  type: 'variableDeductibleInText',
  /** IMPORTANT: Keep values in the right order as they are used to map the deductible to the guarantee */
  values: [
    'Maladie: 15j / Accident: 3j / Hospit.: 3j',
    'Maladie: 15j / Accident: 15j / Hospit.: 15j',
    'Maladie: 30j / Accident: 3j / Hospit.: 3j',
    'Maladie: 30j / Accident: 30j / Hospit.: 30j',
    'Maladie: 60j / Accident: 60j / Hospit.: 60j',
    'Maladie: 90j / Accident: 90j / Hospit.: 90j',
  ] as const,
}

export const frPrelPolicyItemsV1_0_0 = {
  deathBenefitTotalAndPermanentDisability: {
    name: 'deathBenefitTotalAndPermanentDisability',
    type: 'guarantee',
    optionality: ['included'],
    possibleLimitOfIndemnities: { type: 'variableAmount' },
    possibleDeductibles: { type: 'not-applicable' },
    editConfiguration: createPolicyItemConfigurationResolver({
      selfOnboardingConfiguration: () => ({
        isHidden: false,
        canBeUpdated: true,
        canBeSelected: false,
      }),
      backofficeConfiguration: () => ({
        isHidden: false,
        canBeUpdated: true,
        canBeSelected: false,
      }),
    }),
    dependsOn: [],
    additionalInfo: null,
  },
  doubleEffect: {
    name: 'doubleEffect',
    type: 'guarantee',
    optionality: ['included'],
    possibleLimitOfIndemnities: { type: 'variableAmount' },
    possibleDeductibles: { type: 'not-applicable' },
    editConfiguration: createPolicyItemConfigurationResolver({
      selfOnboardingConfiguration: () => ({
        isHidden: true,
        canBeUpdated: false,
        canBeSelected: false,
      }),
      backofficeConfiguration: () => ({
        isHidden: false,
        canBeUpdated: true,
        canBeSelected: true,
      }),
    }),
    dependsOn: [],
    additionalInfo: null,
  },
  accidentalDeathAndPermanentDisability: {
    name: 'accidentalDeathAndPermanentDisability',
    type: 'guarantee',
    optionality: ['optional'],
    possibleLimitOfIndemnities: { type: 'variableAmount' },
    possibleDeductibles: { type: 'not-applicable' },
    editConfiguration: createPolicyItemConfigurationResolver({
      selfOnboardingConfiguration: () => ({
        isHidden: false,
        canBeUpdated: false,
        canBeSelected: true,
      }),
      backofficeConfiguration: () => ({
        isHidden: false,
        canBeUpdated: true,
        canBeSelected: true,
      }),
    }),
    dependsOn: [],
    additionalInfo: null,
  },
  spousePension: {
    name: 'spousePension',
    type: 'guarantee',
    optionality: ['optional'],
    possibleLimitOfIndemnities: {
      type: 'percentages',
      percentages: [tenPercent, twentyPercent],
      baseText: 'du revenu annuel',
    },
    possibleDeductibles: { type: 'not-applicable' },
    editConfiguration: createPolicyItemConfigurationResolver({
      selfOnboardingConfiguration: () => ({
        isHidden: false,
        canBeUpdated: true,
        canBeSelected: true,
      }),
      backofficeConfiguration: () => ({
        isHidden: false,
        canBeUpdated: true,
        canBeSelected: true,
      }),
    }),
    dependsOn: [],
    additionalInfo: null,
  },
  educationAnnuity: {
    name: 'educationAnnuity',
    type: 'guarantee',
    optionality: ['optional'],
    possibleLimitOfIndemnities: {
      type: 'percentages',
      percentages: [twentyPercent, thirtyPercent, fiftyPercent],
      baseText: 'du revenu annuel',
    },
    possibleDeductibles: { type: 'not-applicable' },
    editConfiguration: createPolicyItemConfigurationResolver({
      selfOnboardingConfiguration: () => ({
        isHidden: false,
        canBeUpdated: true,
        canBeSelected: true,
      }),
      backofficeConfiguration: () => ({
        isHidden: false,
        canBeUpdated: true,
        canBeSelected: true,
      }),
    }),
    dependsOn: [],
    additionalInfo: null,
  },
  fearedDiseases: {
    name: 'fearedDiseases',
    type: 'guarantee',
    optionality: ['optional'],
    possibleLimitOfIndemnities: { type: 'variableAmount' },
    possibleDeductibles: { type: 'not-applicable' },
    editConfiguration: createPolicyItemConfigurationResolver({
      selfOnboardingConfiguration: () => ({
        isHidden: false,
        canBeUpdated: false,
        canBeSelected: true,
      }),
      backofficeConfiguration: () => ({
        isHidden: false,
        canBeUpdated: true,
        canBeSelected: true,
      }),
    }),
    dependsOn: [],
    additionalInfo: null,
  },
  totalTemporaryIncapacityWork: {
    name: 'totalTemporaryIncapacityWork',
    type: 'guarantee',
    optionality: ['optional'],
    possibleLimitOfIndemnities: { type: 'variableAmount' },
    possibleDeductibles: {
      type: 'variableDeductibleInText',
      values: [...ittIppDeductible.values],
    },
    editConfiguration: createPolicyItemConfigurationResolver({
      selfOnboardingConfiguration: () => ({
        isHidden: false,
        canBeUpdated: true,
        canBeSelected: true,
      }),
      backofficeConfiguration: () => ({
        isHidden: false,
        canBeUpdated: true,
        canBeSelected: true,
      }),
    }),
    dependsOn: [],
    additionalInfo: null,
  },
  totalPartialPermanantDisability: {
    name: 'totalPartialPermanantDisability',
    type: 'guarantee',
    optionality: ['optional'],
    possibleLimitOfIndemnities: { type: 'variableAmount' },
    possibleDeductibles: { type: 'not-applicable' },
    editConfiguration: ({ state }) => {
      if (!state.birthDate || !state.startDate) {
        return {
          isHidden: false,
          canBeUpdated: false,
          canBeSelected: false,
        }
      }
      const ageAtStartDate = computeAgeFromFutureCalendarDate(state.birthDate, state.startDate)
      if (ageAtStartDate && ageAtStartDate < 60) {
        return {
          isHidden: false,
          canBeUpdated: false,
          canBeSelected: false,
        }
      }
      return {
        isHidden: false,
        canBeUpdated: true,
        canBeSelected: true,
      }
    },
    dependsOn: ['totalTemporaryIncapacityWork'],
    additionalInfo: null,
  },
  premiumExemption: {
    name: 'premiumExemption',
    type: 'guarantee',
    optionality: ['included'],
    possibleLimitOfIndemnities: { type: 'none' },
    possibleDeductibles: { type: 'not-applicable' },
    editConfiguration: createPolicyItemConfigurationResolver({
      selfOnboardingConfiguration: () => ({
        isHidden: true,
        canBeUpdated: false,
        canBeSelected: false,
      }),
      backofficeConfiguration: () => ({
        isHidden: false,
        canBeUpdated: true,
        canBeSelected: false,
      }),
    }),
    dependsOn: [],
    additionalInfo: null,
  },
  redemptionLimitationDiscoPsy: {
    name: 'redemptionLimitationDiscoPsy',
    type: 'guarantee',
    optionality: ['optional'],
    possibleLimitOfIndemnities: { type: 'none' },
    possibleDeductibles: { type: 'not-applicable' },
    editConfiguration: createPolicyItemConfigurationResolver({
      selfOnboardingConfiguration: () => ({
        isHidden: false,
        canBeUpdated: false,
        canBeSelected: true,
      }),
      backofficeConfiguration: () => ({
        isHidden: false,
        canBeUpdated: true,
        canBeSelected: true,
      }),
    }),
    dependsOn: [],
    additionalInfo: null,
  },
  recurringGeneralExpenses: {
    name: 'recurringGeneralExpenses',
    type: 'guarantee',
    optionality: ['optional'],
    possibleLimitOfIndemnities: { type: 'variableAmount' },
    possibleDeductibles: {
      type: 'variableDeductibleInText',
      values: [...ittIppDeductible.values],
    },
    editConfiguration: createPolicyItemConfigurationResolver({
      selfOnboardingConfiguration: () => ({
        isHidden: false,
        canBeUpdated: true,
        canBeSelected: true,
      }),
      backofficeConfiguration: () => ({
        isHidden: false,
        canBeUpdated: true,
        canBeSelected: true,
      }),
    }),
    dependsOn: [],
    additionalInfo: null,
  },
  totalOccupationalDisability: {
    name: 'totalOccupationalDisability',
    type: 'guarantee',
    optionality: ['optional'],
    possibleLimitOfIndemnities: { type: 'variableAmount' },
    possibleDeductibles: { type: 'not-applicable' },
    editConfiguration: createPolicyItemConfigurationResolver({
      selfOnboardingConfiguration: () => ({
        isHidden: false,
        canBeUpdated: false,
        canBeSelected: true,
      }),
      backofficeConfiguration: () => ({
        isHidden: false,
        canBeUpdated: true,
        canBeSelected: true,
      }),
    }),
    dependsOn: [],
    additionalInfo: null,
  },
} as const satisfies PolicyItems<FrPrelPolicyItems, FrPrelPolicyItemsState>
